import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  Suspense,
} from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  Grid,
  Button,
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Article as DocIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";
import Layout from "../components/layout/Layout";
import config from "../config.js";

const FilePreview = React.memo(({ file, onDownload }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [previewError, setPreviewError] = useState(false);

  if (!file) return <Typography>No file selected</Typography>;

  const { fileUrl, fileName } = file;
  const fileType = fileName.split(".").pop().toLowerCase();

  const renderPreview = () => {
    if (fileType === "pdf") {
      return (
        <object
          data={fileUrl}
          type="application/pdf"
          style={{
            width: "100%",
            height: "100%",
            minHeight: "500px",
          }}
        >
          <Typography>PDF preview not available</Typography>
        </object>
      );
    }

    if (["jpg", "jpeg", "png", "gif"].includes(fileType)) {
      return (
        <img
          src={fileUrl}
          alt={fileName}
          style={{ maxWidth: "100%", height: "auto" }}
          onLoad={() => setIsLoading(false)}
          onError={() => setPreviewError(true)}
        />
      );
    }

    const googleDocsViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
      fileUrl
    )}&embedded=true`;

    return (
      <iframe
        src={googleDocsViewerUrl}
        style={{
          width: "100%",
          height: "100%",
          minHeight: "500px",
          border: "none",
        }}
        title={`Preview of ${fileName}`}
        onLoad={() => setIsLoading(false)}
        onError={() => setPreviewError(true)}
      />
    );
  };

  return (
    <Paper
      sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">{fileName}</Typography>
        <Button
          sx={{
            backgroundColor: "#EA5455",
            color: "white",
            "&:hover": {
              backgroundColor: "#EA5455",
              opacity: 0.9,
            },
          }}
          startIcon={<DownloadIcon />}
          onClick={() => onDownload(fileUrl)}
        >
          Download
        </Button>
      </Box>
      <Box
        sx={{ flexGrow: 1, bgcolor: "background.paper", position: "relative" }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {previewError ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              minHeight: "500px",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography color="error" sx={{ mb: 2 }}>
                Preview failed to load
              </Typography>
              <Button
                color="primary"
                onClick={() => window.open(fileUrl, "_blank")}
              >
                Open file directly
              </Button>
            </Box>
          </Box>
        ) : (
          renderPreview()
        )}
      </Box>
    </Paper>
  );
});

const UserDocument = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const { API_BASE_URL } = config;
  const kycId = useSelector((state) => state.kyc.selectedCompanyId);
  const [previewCache] = useState(new Map());

  const fetchFiles = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getClientFilesDetails/${kycId}`
      );
      if (response.data.success && Array.isArray(response.data.data?.files)) {
        setFiles(response.data.data.files);
      } else {
        setFiles([]);
        setError("No files found or invalid response");
      }
    } catch (err) {
      setError("Error fetching files: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, kycId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const getFileType = useCallback((fileUrl) => {
    const parts = fileUrl.split("/Dashboard");
    return parts[parts.length - 2];
  }, []);

  const getFileIcon = useCallback((fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    if (["xlsx", "xls"].includes(extension)) {
      return <ExcelIcon color="gray" />;
    }
    if (["pdf"].includes(extension)) {
      return <PdfIcon color="gray" />;
    }
    if (["doc", "docx"].includes(extension)) {
      return <DocIcon color="gray" />;
    }
    return <FileIcon color="gray" />;
  }, []);

  const handleFileClick = useCallback(
    (fileNode) => {
      if (fileNode.type === "file") {
        const cachedPreview = previewCache.get(fileNode.url);
        if (cachedPreview) {
          setSelectedFile(cachedPreview);
        } else {
          const newPreview = {
            fileUrl: fileNode.url,
            fileName: fileNode.name,
          };
          previewCache.set(fileNode.url, newPreview);
          setSelectedFile(newPreview);
        }
      }
    },
    [previewCache]
  );

  const handleDownload = useCallback((fileUrl) => {
    window.open(fileUrl, "_blank");
  }, []);

  const toggleNode = useCallback((path) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  }, []);

  const createFileTree = useCallback(() => {
    const tree = {};
    files.forEach((file) => {
      const fileType = getFileType(file.fileUrl);

      if (!tree[file.category]) {
        tree[file.category] = {
          name: file.category,
          type: "folder",
          children: {},
        };
      }

      if (!tree[file.category].children[file.subCategory]) {
        tree[file.category].children[file.subCategory] = {
          name: file.subCategory,
          type: "folder",
          children: {},
        };
      }

      if (!tree[file.category].children[file.subCategory].children[fileType]) {
        tree[file.category].children[file.subCategory].children[fileType] = {
          name: fileType,
          type: "folder",
          children: {},
        };
      }

      tree[file.category].children[file.subCategory].children[
        fileType
      ].children[file._id] = {
        name: file.fileName,
        type: "file",
        id: file._id,
        url: file.fileUrl,
        uploadDate: new Date(file.uploadDate).toLocaleDateString(),
      };
    });
    return tree;
  }, [files, getFileType]);

  const renderNode = useCallback(
    (node, path = "", level = 0) => {
      const isExpanded = expandedNodes[path];
      const hasChildren =
        node.type === "folder" && Object.keys(node.children || {}).length > 0;

      return (
        <Box key={path}>
          <ListItem disablePadding sx={{ pl: level * 2 }}>
            <ListItemButton
              onClick={() => {
                if (hasChildren) {
                  toggleNode(path);
                } else if (node.type === "file") {
                  handleFileClick(node);
                }
              }}
              dense
              selected={selectedFile?.fileName === node.name}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {hasChildren ? (
                  isExpanded ? (
                    <ExpandMoreIcon />
                  ) : (
                    <ChevronRightIcon />
                  )
                ) : null}
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {node.type === "folder" ? (
                  <FolderIcon sx={{ color: "#EA5455" }} />
                ) : (
                  getFileIcon(node.name)
                )}
              </ListItemIcon>
              <Tooltip title={node.uploadDate || ""} placement="right">
                <ListItemText
                  primary={node.name}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: {
                      fontWeight: node.type === "folder" ? 500 : 400,
                    },
                  }}
                />
              </Tooltip>
            </ListItemButton>
          </ListItem>

          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List disablePadding>
                {Object.entries(node.children).map(([childKey, childNode]) =>
                  renderNode(
                    childNode,
                    path ? `${path}/${childNode.name}` : childNode.name,
                    level + 1
                  )
                )}
              </List>
            </Collapse>
          )}
        </Box>
      );
    },
    [expandedNodes, selectedFile, handleFileClick, toggleNode, getFileIcon]
  );

  const fileTree = useMemo(() => createFileTree(), [createFileTree]);

  if (loading) {
    return (
      <Layout>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Paper>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Paper elevation={2} sx={{ p: 2 }}>
          <Alert severity="error">
            <h1>No company selected</h1>
          </Alert>
        </Paper>
      </Layout>
    );
  }

  return (
    <Layout>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper
            elevation={2}
            sx={{
              height: "100%",
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Files
              </Typography>
            </Box>
            <List disablePadding>
              {Object.values(fileTree).map((node) =>
                renderNode(node, node.name)
              )}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Suspense fallback={<CircularProgress />}>
            <FilePreview file={selectedFile} onDownload={handleDownload} />
          </Suspense>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default UserDocument;
